import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { OwnerInfoForm } from "./OwnerInfoForm";
import { PropertyInfoForm } from "./PropertyInfoForm";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface Owner {
  id: string;
  name: string;
  email: string;
  phone: string;
  whatsapp: string;
  properties: any[];
  registrationDate: Date;
  lastUpdateDate: Date;
}

interface ProprietarioFormProps {
  owner?: Owner;
  isEditing?: boolean;
  ownerId?: string;
  onSubmit?: (data: any) => Promise<void>;
}

export default function ProprietarioForm({ owner, isEditing = false }: ProprietarioFormProps) {
  const [nome, setNome] = useState(owner?.name || "");
  const [email, setEmail] = useState(owner?.email || "");
  const [telefone, setTelefone] = useState(owner?.phone || "");
  const [whatsapp, setWhatsapp] = useState(owner?.whatsapp || "");
  const [website, setWebsite] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [properties, setProperties] = useState<any[]>([]);
  
  // Property information
  const [propertyTitle, setPropertyTitle] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [propertyType, setPropertyType] = useState("sale");
  const [salePrice, setSalePrice] = useState("");
  const [rentPrice, setRentPrice] = useState("");
  const [seasonalPrice, setSeasonalPrice] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [area, setArea] = useState("");
  const [landArea, setLandArea] = useState("");
  const [description, setDescription] = useState("");
  
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (owner) {
      setNome(owner.name);
      setEmail(owner.email);
      setTelefone(owner.phone);
      setWhatsapp(owner.whatsapp);
    }
    fetchProperties();
  }, [owner]);

  const fetchProperties = async () => {
    try {
      const { data: propertiesData, error } = await supabase
        .from('properties')
        .select('*');
      
      if (error) throw error;
      setProperties(propertiesData || []);
    } catch (error: any) {
      console.error('Error fetching properties:', error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar imóveis",
        description: "Não foi possível carregar a lista de imóveis.",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // First, create or update the owner
      const ownerData = {
        name: nome,
        email,
        phone: telefone,
        whatsapp,
        lastUpdateDate: new Date().toISOString(),
        ...(isEditing ? {} : { registrationDate: new Date().toISOString() })
      };

      let ownerId = owner?.id;
      let ownerResponse;

      if (isEditing && ownerId) {
        ownerResponse = await supabase
          .from('owners')
          .update(ownerData)
          .eq('id', ownerId)
          .select()
          .single();
      } else {
        ownerResponse = await supabase
          .from('owners')
          .insert([ownerData])
          .select()
          .single();
      }

      if (ownerResponse.error) throw ownerResponse.error;
      ownerId = ownerResponse.data.id;

      // Then, handle property information if provided
      if (propertyTitle || selectedPropertyId) {
        const propertyData = {
          title: propertyTitle,
          address: propertyAddress,
          type: propertyType,
          sale_price: salePrice ? parseFloat(salePrice) : null,
          rent_price: rentPrice ? parseFloat(rentPrice) : null,
          seasonal_price: seasonalPrice ? parseFloat(seasonalPrice) : null,
          bedrooms: bedrooms ? parseInt(bedrooms) : null,
          bathrooms: bathrooms ? parseInt(bathrooms) : null,
          area: area ? parseFloat(area) : null,
          land_area: landArea ? parseFloat(landArea) : null,
          description,
          owner_id: ownerId,
          website_url: website || null
        };

        if (selectedPropertyId) {
          const { error: propertyError } = await supabase
            .from('properties')
            .update(propertyData)
            .eq('id', selectedPropertyId);

          if (propertyError) throw propertyError;
        } else {
          const { error: propertyError } = await supabase
            .from('properties')
            .insert([propertyData]);

          if (propertyError) throw propertyError;
        }
      }

      toast({
        title: "Sucesso!",
        description: isEditing 
          ? "Proprietário atualizado com sucesso!"
          : "Proprietário cadastrado com sucesso!",
      });

      navigate("/proprietarios");
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast({
        variant: "destructive",
        title: "Erro ao salvar",
        description: error.message,
      });
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      toast({
        title: "Arquivo recebido",
        description: "O arquivo CSV está sendo processado...",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => document.getElementById("csv-upload")?.click()}>
          <Upload className="w-4 h-4 mr-2" />
          Importar CSV
          <Input
            id="csv-upload"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileUpload}
          />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <OwnerInfoForm
            nome={nome}
            email={email}
            telefone={telefone}
            whatsapp={whatsapp}
            website={website}
            selectedPropertyId={selectedPropertyId}
            properties={properties}
            setNome={setNome}
            setEmail={setEmail}
            setTelefone={setTelefone}
            setWhatsapp={setWhatsapp}
            setWebsite={setWebsite}
            setSelectedPropertyId={setSelectedPropertyId}
          />

          <PropertyInfoForm
            propertyTitle={propertyTitle}
            propertyAddress={propertyAddress}
            propertyType={propertyType}
            salePrice={salePrice}
            rentPrice={rentPrice}
            seasonalPrice={seasonalPrice}
            bedrooms={bedrooms}
            bathrooms={bathrooms}
            area={area}
            landArea={landArea}
            description={description}
            setPropertyTitle={setPropertyTitle}
            setPropertyAddress={setPropertyAddress}
            setPropertyType={setPropertyType}
            setSalePrice={setSalePrice}
            setRentPrice={setRentPrice}
            setSeasonalPrice={setSeasonalPrice}
            setBedrooms={setBedrooms}
            setBathrooms={setBathrooms}
            setArea={setArea}
            setLandArea={setLandArea}
            setDescription={setDescription}
          />
        </div>

        <Button type="submit" className="w-full">
          <Save className="w-4 h-4 mr-2" />
          {isEditing ? "Salvar Alterações" : "Cadastrar Proprietário e Imóvel"}
        </Button>
      </form>
    </div>
  );
}